import React from 'react';
import { useTheme, useUIColors } from '../../hooks/useTheme';

const renderIcon = (icon, size = 14) => {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  const Icon = icon;
  return <Icon size={size} />;
};

export const PillTabBar = ({
  items = [],
  activeKey,
  onChange,
  ariaLabel = 'Pestañas',
  style = {},
}) => {
  const ui = useUIColors();
  const accent = ui.interactive || '#16a34a';
  const accentText = ui.textOnAccent || '#fff';

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, ...style }}
    >
      {items.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange?.(item.key)}
            style={{
              height: 32,
              padding: '0 12px',
              borderRadius: 999,
              border: `1px solid ${isActive ? accent : ui.border}`,
              background: isActive ? accent : 'transparent',
              color: isActive ? accentText : ui.text,
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              fontSize: 12,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              opacity: item.disabled ? 0.5 : 1,
            }}
          >
            {renderIcon(item.icon, 14)}
            {item.label}
            {item.badge != null && item.badge !== '' ? (
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: isActive ? 'rgba(255,255,255,0.22)' : accent,
                  color: accentText,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {item.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export const SegmentedControl = ({
  items = [],
  activeKey,
  onChange,
  ariaLabel = 'Opciones',
  style = {},
}) => {
  const theme = useTheme();
  const colors = theme.colors || {};
  const text = colors.text || {};
  const border = colors.border || {};
  const interactive = colors.interactive || {};

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: 3,
        backgroundColor: colors.secondary,
        border: `1px solid ${border.primary}`,
        borderRadius: 10,
        ...style,
      }}
    >
      {items.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange?.(item.key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              border: 'none',
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              padding: '6px 10px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              opacity: item.disabled ? 0.5 : 1,
              transition: 'background-color 0.2s ease, color 0.2s ease',
              backgroundColor: isActive ? interactive.primary : 'transparent',
              color: isActive ? text.accent : text.secondary,
            }}
          >
            {renderIcon(item.icon, 14)}
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

export const VerticalTabMenu = ({
  items = [],
  activeKey,
  onChange,
  ariaLabel = 'Secciones',
}) => {
  const ui = useUIColors();
  const accent = ui.interactive || '#16a34a';

  return (
    <nav
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        paddingRight: 12,
        borderRight: `1px solid ${ui.border}`,
      }}
    >
      {items.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onChange?.(item.key)}
            style={{
              width: '100%',
              minHeight: 36,
              padding: '8px 12px',
              border: 'none',
              borderLeft: isActive ? `3px solid ${accent}` : '3px solid transparent',
              borderRadius: '0 8px 8px 0',
              background: isActive ? `${accent}14` : 'transparent',
              color: isActive ? ui.text : ui.muted,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: isActive ? 700 : 500,
              textAlign: 'left',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {renderIcon(item.icon, 15)}
            {item.label}
          </button>
        );
      })}
    </nav>
  );
};

export const SettingsSplit = ({
  items = [],
  activeKey,
  onChange,
  ariaLabel = 'Catálogos',
  children,
}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '200px minmax(0, 1fr)',
      gap: 16,
      alignItems: 'start',
    }}
  >
    <VerticalTabMenu items={items} activeKey={activeKey} onChange={onChange} ariaLabel={ariaLabel} />
    <div>{children}</div>
  </div>
);

export default PillTabBar;
